import ReactECharts from 'echarts-for-react';
import stackData from '../data/stackDependence.json';
import { useIsNarrow } from '../../hooks/useIsNarrow';

/**
 * ImportDependenceChart — China's share of U.S. imports, aggregate vs by input
 * (Fig 1). Horizontal bar. The aggregate figure is a reference line, not a bar,
 * because the whole point is that the headline number is the wrong denominator:
 * 9% of all imports, 37% of rare earths.
 *
 * All values come from stackDependence.json (Rule 27); nothing is typed twice.
 */

const BRAND = {
  orange: '#FD9800',
  muted: '#9CA3AF',
  mutedDark: '#6B7280',
  surface: '#171717',
};

type ImportItem = { category: string; short: string; sharePct: number; chokepoint: boolean };

const fmt = (v: number) => `${v}%`;

export const ImportDependenceChart = ({ height = 320 }: { height?: number }) => {
  const narrow = useIsNarrow();
  const fig = (
    stackData as unknown as {
      figures: {
        import_dependence: {
          source: string;
          note: string;
          unit: string;
          aggregatePct: number;
          aggregatePriorPct: number;
          items: ImportItem[];
        };
      };
    }
  ).figures.import_dependence;

  const aggregate = fig.aggregatePct;

  const option = {
    backgroundColor: BRAND.surface,
    animation: false,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (params: { dataIndex: number }[]) => {
        const item = fig.items[params[0].dataIndex];
        return `${item.category}<br/><b>${fmt(item.sharePct)}</b> of U.S. imports in that category come from China<br/><span style="color:${BRAND.muted}">All U.S. imports, combined: ${fmt(aggregate)}</span>`;
      },
    },
    grid: { left: narrow ? 92 : 118, right: narrow ? 44 : 56, top: 16, bottom: narrow ? 28 : 32 },
    xAxis: {
      type: 'value',
      max: 40,
      axisLabel: { color: BRAND.muted, fontSize: narrow ? 11 : 12, formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    yAxis: {
      type: 'category',
      data: fig.items.map((i) => i.short),
      inverse: true,
      axisLine: { lineStyle: { color: '#333' } },
      axisTick: { show: false },
      axisLabel: { color: '#e5e5e5', fontSize: narrow ? 11 : 13, fontWeight: 700 },
    },
    series: [
      {
        type: 'bar',
        barWidth: '54%',
        data: fig.items.map((i) => ({
          value: i.sharePct,
          itemStyle: {
            color: i.chokepoint ? BRAND.orange : BRAND.mutedDark,
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true,
          position: 'right',
          color: '#e5e5e5',
          fontWeight: 700,
          fontSize: narrow ? 11 : 12,
          formatter: (p: { value: number }) => fmt(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: BRAND.muted, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: BRAND.muted,
            fontSize: 11,
            fontWeight: 700,
            formatter: `all imports: ${fmt(aggregate)}`,
          },
          data: [{ xAxis: aggregate }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 1. China&apos;s share of U.S. imports in 2025, by category against the {fmt(aggregate)}{' '}
        aggregate (down from {fmt(fig.aggregatePriorPct)} in 2017). The headline fell; three of the four
        inputs an AI buildout needs did not fall with it. {fig.note}
      </p>
    </div>
  );
};
